import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import '../styles/Buttons.css';

interface BulkEditModalProps {
  isOpen: boolean;
  workspaceId: string;
  projectId: string;
  entityType: 'parking' | 'locker' | 'bicycle';
  spaces: any[];
  availableTypes: any[];
  availableAddons: any[];
  onClose: () => void;
  onSave: (updates: any[]) => Promise<void>;
}

interface AddonRef {
  _id: string;
  name: string;
  price?: number;
  color?: string;
  mandatory?: boolean;
}

interface SpaceEdit {
  _id: string;
  number: string;
  type: string;
  status: string;
  isPremium: boolean;
  addons: AddonRef[];
  mandatoryAddons: AddonRef[];
}

const BulkEditModal: React.FC<BulkEditModalProps> = ({
  isOpen,
  workspaceId,
  projectId,
  entityType,
  spaces,
  availableTypes,
  availableAddons,
  onClose,
  onSave
}) => {
  const [spaceEdits, setSpaceEdits] = useState<SpaceEdit[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen && spaces.length > 0) {
      // Initialize space edits from existing spaces
      const initialEdits: SpaceEdit[] = spaces.map(space => ({
        _id: space._id,
        number: space.number,
        type: space.type,
        status: space.status,
        isPremium: space.isPremium || false,
        addons: space.addons || [],
        mandatoryAddons: space.mandatoryAddons || []
      }));
      setSpaceEdits(initialEdits);
    }
  }, [isOpen, spaces]);

  const handleFieldChange = (index: number, field: keyof SpaceEdit, value: any) => {
    const updatedEdits = [...spaceEdits];
    updatedEdits[index] = {
      ...updatedEdits[index],
      [field]: value
    };
    setSpaceEdits(updatedEdits);
  };

  const handleAddonAdd = (index: number, addonId: string, isMandatory: boolean) => {
    if (!addonId) return;

    const addon = availableAddons.find(a => a._id === addonId);
    if (!addon) return;

    const space = spaceEdits[index];

    // Check if addon already exists in either list
    if (space.addons.some(a => a._id === addonId) || space.mandatoryAddons.some(a => a._id === addonId)) {
      return;
    }

    const updatedEdits = [...spaceEdits];
    if (isMandatory) {
      updatedEdits[index].mandatoryAddons = [
        ...updatedEdits[index].mandatoryAddons,
        { _id: addon._id, name: addon.name, price: addon.price, color: addon.color, mandatory: true }
      ];
    } else {
      updatedEdits[index].addons = [
        ...updatedEdits[index].addons,
        { _id: addon._id, name: addon.name, price: addon.price, color: addon.color }
      ];
    }
    setSpaceEdits(updatedEdits);
  };

  const handleAddonRemove = (index: number, addonId: string, isMandatory: boolean) => {
    const updatedEdits = [...spaceEdits];
    if (isMandatory) {
      updatedEdits[index].mandatoryAddons = updatedEdits[index].mandatoryAddons.filter(a => a._id !== addonId);
    } else {
      updatedEdits[index].addons = updatedEdits[index].addons.filter(a => a._id !== addonId);
    }
    setSpaceEdits(updatedEdits);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      // Prepare update data - send 'id' field as backend expects it
      const updates = spaceEdits.map(edit => {
        const original = spaces.find(s => s._id === edit._id);
        const changes: any = { id: edit._id }; // Backend expects 'id' not '_id'

        if (original) {
          if (edit.type !== original.type) changes.type = edit.type;
          if (edit.status !== original.status) changes.status = edit.status;
          if (edit.isPremium !== original.isPremium) changes.isPremium = edit.isPremium;

          // Always include addons since they may have changed
          changes.addons = edit.addons;
          changes.mandatoryAddons = edit.mandatoryAddons;
        }

        return changes;
      });

      await onSave(updates);
      onClose();
    } catch (error) {
      console.error('Error saving bulk edits:', error);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  const getEntityLabel = () => {
    switch (entityType) {
      case 'parking': return 'Parking';
      case 'locker': return 'Locker';
      case 'bicycle': return 'Bicycle';
      default: return 'Space';
    }
  };

  return (
    <div className="modal-overlay">
      <div
        className="modal-container"
        style={{
          maxWidth: '90vw',
          width: '90vw',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
        }}
      >
        <div className="modal-header">
          <h2>Bulk Edit {getEntityLabel()} Spaces</h2>
          <button onClick={onClose} className="modal-close">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <div className="modal-body" style={{ flex: 1, overflowY: 'auto', padding: '20px', minHeight: 0 }}>
            <p style={{ fontSize: '14px', color: '#6B7280', marginBottom: '16px' }}>
              Edit multiple spaces at once. Changes will be saved when you click "Save Changes".
            </p>

            <div style={{ overflowX: 'auto', width: '100%' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px', tableLayout: 'fixed' }}>
                <thead>
                  <tr style={{
                    backgroundColor: '#48182F',
                    color: 'white',
                    position: 'sticky',
                    top: 0,
                    zIndex: 10
                  }}>
                    <th style={{ padding: '12px', textAlign: 'left', width: '8%' }}>Space #</th>
                    <th style={{ padding: '12px', textAlign: 'left', width: '16%' }}>Type</th>
                    <th style={{ padding: '12px', textAlign: 'left', width: '16%' }}>Status</th>
                    <th style={{ padding: '12px', textAlign: 'center', width: '10%' }}>Premium</th>
                    <th style={{ padding: '12px', textAlign: 'left', width: '25%' }}>Addons</th>
                    <th style={{ padding: '12px', textAlign: 'left', width: '25%' }}>Mandatory Addons</th>
                  </tr>
                </thead>
                <tbody>
                  {spaceEdits.map((space, index) => (
                    <tr
                      key={space._id}
                      style={{
                        borderBottom: '1px solid #E5E7EB',
                        backgroundColor: index % 2 === 0 ? '#FFFFFF' : '#F9FAFB'
                      }}
                    >
                      <td style={{ padding: '12px', fontWeight: '600', color: '#48182F', width: '8%' }}>
                        {space.number}
                      </td>
                      <td style={{ padding: '12px', width: '16%' }}>
                        <select
                          className="form-input"
                          value={space.type}
                          onChange={(e) => handleFieldChange(index, 'type', e.target.value)}
                          style={{ fontSize: '14px', padding: '8px', width: '100%' }}
                        >
                          {availableTypes.map((type) => (
                            <option key={type._id} value={type.name}>
                              {type.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td style={{ padding: '12px', width: '16%' }}>
                        <select
                          className="form-input"
                          value={space.status}
                          onChange={(e) => handleFieldChange(index, 'status', e.target.value)}
                          style={{ fontSize: '14px', padding: '8px', width: '100%' }}
                        >
                          <option value="AVAILABLE">Available</option>
                          <option value="MARKETING HOLD">Marketing Hold</option>
                          <option value="SALES HOLD">Sales Hold</option>
                          <option value="CUSTOMER SELECTION">Customer Selection</option>
                          <option value="CUSTOMER SUBMITTED">Customer Submitted</option>
                          <option value="SOLD">Sold</option>
                        </select>
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center', width: '10%' }}>
                        <input
                          type="checkbox"
                          checked={space.isPremium}
                          onChange={(e) => handleFieldChange(index, 'isPremium', e.target.checked)}
                          style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                        />
                      </td>
                      <td style={{ padding: '12px', width: '25%' }}>
                        <div>
                          <select
                            className="form-input"
                            onChange={(e) => { handleAddonAdd(index, e.target.value, false); e.target.value = ''; }}
                            style={{ fontSize: '14px', padding: '8px', width: '100%', marginBottom: '8px' }}
                            defaultValue=""
                          >
                            <option value="">Add addon...</option>
                            {availableAddons.map((addon) => (
                              <option
                                key={addon._id}
                                value={addon._id}
                                disabled={space.addons.some(a => a._id === addon._id) || space.mandatoryAddons.some(a => a._id === addon._id)}
                              >
                                {addon.name}
                              </option>
                            ))}
                          </select>
                          {space.addons.length > 0 && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                              {space.addons.map((addon) => (
                                <div
                                  key={addon._id}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    padding: '6px 10px',
                                    backgroundColor: '#E5E7EB',
                                    borderRadius: '4px',
                                    fontSize: '13px'
                                  }}
                                >
                                  <span>{addon.name}</span>
                                  <button
                                    type="button"
                                    onClick={() => handleAddonRemove(index, addon._id, false)}
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      cursor: 'pointer',
                                      padding: '0',
                                      display: 'flex',
                                      alignItems: 'center',
                                      color: '#6B7280'
                                    }}
                                  >
                                    <X size={14} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: '12px', width: '25%' }}>
                        <div>
                          <select
                            className="form-input"
                            onChange={(e) => { handleAddonAdd(index, e.target.value, true); e.target.value = ''; }}
                            style={{ fontSize: '14px', padding: '8px', width: '100%', marginBottom: '8px' }}
                            defaultValue=""
                          >
                            <option value="">Add mandatory addon...</option>
                            {availableAddons.map((addon) => (
                              <option
                                key={addon._id}
                                value={addon._id}
                                disabled={space.addons.some(a => a._id === addon._id) || space.mandatoryAddons.some(a => a._id === addon._id)}
                              >
                                {addon.name}
                              </option>
                            ))}
                          </select>
                          {space.mandatoryAddons.length > 0 && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                              {space.mandatoryAddons.map((addon) => (
                                <div
                                  key={addon._id}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    padding: '6px 10px',
                                    backgroundColor: '#FEF3C7',
                                    borderRadius: '4px',
                                    fontSize: '13px',
                                    color: '#92400E'
                                  }}
                                >
                                  <span>{addon.name}</span>
                                  <button
                                    type="button"
                                    onClick={() => handleAddonRemove(index, addon._id, true)}
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      cursor: 'pointer',
                                      padding: '0',
                                      display: 'flex',
                                      alignItems: 'center',
                                      color: '#D97706'
                                    }}
                                  >
                                    <X size={14} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="modal-footer" style={{
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end',
            padding: '16px 24px',
            borderTop: '1px solid #e5e7eb',
            background: 'white'
          }}>
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
              disabled={isSaving}
              style={{
                padding: '10px 20px',
                fontSize: '14px',
                fontWeight: '500',
                borderRadius: '6px',
                cursor: isSaving ? 'not-allowed' : 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSaving}
              style={{
                padding: '10px 20px',
                fontSize: '14px',
                fontWeight: '500',
                borderRadius: '6px',
                cursor: isSaving ? 'not-allowed' : 'pointer'
              }}
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BulkEditModal;
